import { objectIdString } from "@Helpers/schemas/common.schema";

/**
 * A page inside one project. Matched on the route's PATTERN, not on its `params` rules: the portal-settings Project
 * Users page (`/portalSettings/:settingType/:projectId/projectUsers`) declares an ObjectId `projectId` too, and
 * loading that project as a member (what `?projectId` makes Router.tsx do) would show Forbidden to a portal admin who
 * is not in it.
 */
const PROJECT_PAGE = /^\/project\/:projectId(\/|$)/;

export interface CanonicalSearchInput {
  /** The route path that matched, as routes.tsx declares it (`/project/:projectId/listTask`). */
  pattern: string;
  /** The URL's path params (`useParams()`). */
  params: Readonly<Record<string, string | undefined>>;
  /** The URL's query. Read only. */
  search: URLSearchParams;
  /** The route entry's `portalList` flag (routeTypes.ts). */
  portalList?: true | undefined;
}

/**
 * The query a URL should carry instead of its own, or `null` when it already carries the right one.
 *
 * - A project page whose query has no valid `projectId` gets the path's id: Router.tsx loads the project only from
 *   `?projectId`. Without it, the project pages gated on `useUserModuleAccess().ready` never render (they wait for
 *   the project sidebar, which mounts only with it), and the rest render without their project: no breadcrumb or
 *   title, and no Forbidden for a user who is not a member.
 * - A portal-wide list (`portalList`) opened with neither a path project nor a `?projectId` gets `byPortal=true`, which
 *   is how its pages tell the portal list from a project's. Only the exact value `"true"` is left alone, because the
 *   pages read it differently (TaskList wants exactly `"true"`, the others any non-empty value).
 *
 * Every other param keeps its place; `set` replaces a malformed `projectId` where it stands. The two cases never both
 * apply (one needs a path project, the other none), and the query each returns passes its own check, so following the
 * answer once is final.
 */
export function canonicalSearch({ pattern, params, search, portalList }: CanonicalSearchInput): string | null {
  const pathProjectId = objectIdString.safeParse(params.projectId);
  if (
    PROJECT_PAGE.test(pattern) &&
    pathProjectId.success &&
    !objectIdString.safeParse(search.get("projectId")).success
  ) {
    const next = new URLSearchParams(search);
    next.set("projectId", pathProjectId.data);
    return next.toString();
  }
  if (portalList && params.projectId === undefined && !search.get("projectId") && search.get("byPortal") !== "true") {
    const next = new URLSearchParams(search);
    next.set("byPortal", "true");
    return next.toString();
  }
  return null;
}
